import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Tenant, tenantService } from '../services/tenantService';
import { useAuth } from './AuthContext';

interface TenantContextType {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  setCurrentTenant: (tenant: Tenant | null) => void;
  loading: boolean;
  error: string | null;
  refreshTenant: () => void;
  refreshKey: number;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch real tenant data when user logs in
  useEffect(() => {
    if (user?.tenant_id) {
      tenantService.getMyTenant().then(setCurrentTenant).catch(() => {
        setCurrentTenant({ id: user.tenant_id!, name: '', slug: '', description: null, contact_email: null, contact_phone: null, address: null, is_active: true, created_at: '', updated_at: '' });
      });
    } else {
      setCurrentTenant(null);
    }
  }, [user]);

  const refreshTenant = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        availableTenants: currentTenant ? [currentTenant] : [],
        setCurrentTenant,
        loading: false,
        error: null,
        refreshTenant,
        refreshKey,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};